import React, { useState } from 'react';
import styled from 'styled-components';
import PropTypes from 'prop-types';
import { useIntl, defineMessages } from 'react-intl';
import { useModal } from 'components/shared/hooks';
import { Modal } from 'components/core/modal/constants';
import GalleryContainer from 'components/assets/GalleryContainer';
import { useRemoveTaskImage } from '../hooks';
import { usePhotoOptimisticEdits } from 'javascripts/hooks';

const messages = defineMessages({
  removePhotoFromTask: {
    id: 'Projects.taskList.removePhotoFromTask',
    defaultMessage: 'Remove From Field',
    description: 'Tooltip hover text on the delete icon on a photo in a task',
  },
  taskImageDeleteTitle: {
    id: 'Projects.taskList.taskImageDeleteModal.taskImageDeleteTitle',
    defaultMessage: 'Remove Photo From Field',
    description: 'title text displayed when deleting a task photo',
  },
  taskImageDeleteBody: {
    id: 'Projects.taskList.taskImageDeleteModal.taskImageDeleteBody',
    defaultMessage: `Are you sure you want to remove this photo from the field?
  This will not delete the photo from the project entirely.`,
    description: 'banner text displayed next to task photo delete icon',
  },
  taskImageDeleteConfirm: {
    id: 'Projects.taskList.taskImageDeleteModal.taskImageDeleteConfirm',
    defaultMessage: 'Yes, Remove',
    description: 'task photo button delete confirmation text',
  },
  taskImageDeleteCancel: {
    id: 'Projects.taskList.taskImageDeleteModal.taskImageDeleteCancel',
    defaultMessage: "No, Don't Remove",
    description: 'task photo button delete cancel text',
  },
});

// #region styled-components
const TaskPhotosContainer = styled.div`
  display: grid;
  grid-gap: 8px;
  grid-template-columns: repeat(auto-fill, minmax(110px, 1fr));
  margin: 16px 28px 8px 34px;

  &:empty {
    display: none;
  }
`;

const DeletePhotoIcon = styled.div`
  position: absolute;
  top: 8px;
  right: 8px;
  width: 24px;
  height: 24px;
  border-radius: 50%;
  color: white;
  cursor: pointer;
  z-index: 10;
  opacity: 0;

  i {
    font-size: 24px;
  }
`;

const PhotoContainer = styled.div`
  position: relative;
  width: 100%;
  height: 0;
  padding-bottom: 100%;

  &::after {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    background: var(--cc_color_background_backdrop_alpha_90);
    border-radius: var(--cc_size_border_radius_l);
    pointer-events: none;
    opacity: 0;
  }

  &:hover {
    &::after {
      opacity: 1;
    }

    ${DeletePhotoIcon} {
      opacity: 1;
    }
  }
`;

const Photo = styled.img`
  position: absolute;
  width: 100%;
  height: 100%;
  border-radius: var(--cc_size_border_radius_l);
  object-fit: cover;
  object-position: center;
  cursor: pointer;
`;
// #endregion

const TaskAssets = ({ task = {}, toggleTaskStatus }) => {
  const intl = useIntl();
  const { showModal, hideModal } = useModal();
  const { removeTaskImage } = useRemoveTaskImage({
    onCompleted: ({ updatedTask }) => {
      toggleTaskStatus(updatedTask);
    },
  });

  const { getOptimisticUri } = usePhotoOptimisticEdits();

  const [initialGalleryAsset, setInitialGalleryAsset] = useState(null);
  const [isGalleryOpen, setIsGalleryOpen] = useState(false);

  const taskImages = task?.images?.nodes ?? [];

  const onRequestClose = () => {
    hideModal(Modal.CONFIRMATION);
  };

  const confirmRemove = ({ id, imageId }) => {
    removeTaskImage({ variables: { input: { id, imageId } } }).then(
      onRequestClose(),
    );
  };

  return (
    <>
      <TaskPhotosContainer>
        {taskImages.map((taskImage) => (
          <PhotoContainer key={taskImage.id}>
            <DeletePhotoIcon
              onClick={() =>
                showModal(Modal.CONFIRMATION, {
                  confirmAction: () =>
                    confirmRemove({
                      id: task.id,
                      imageId: taskImage.id,
                    }),
                  confirmLabel: intl.formatMessage(
                    messages.taskImageDeleteConfirm,
                  ),
                  cancelLabel: intl.formatMessage(
                    messages.taskImageDeleteCancel,
                  ),
                  message: intl.formatMessage(messages.taskImageDeleteBody),
                  title: intl.formatMessage(messages.taskImageDeleteTitle),
                  onRequestClose,
                })
              }
              className="hint hint--left"
              aria-label={intl.formatMessage(messages.removePhotoFromTask)}
            >
              <i className="mdi mdi-minus-circle" />
            </DeletePhotoIcon>
            <Photo
              key={taskImage.id}
              src={
                getOptimisticUri(taskImage.databaseId) ||
                taskImage.assetPreviewThumbnail
              }
              onClick={() => {
                setIsGalleryOpen(true);
                setInitialGalleryAsset(taskImage);
              }}
            />
          </PhotoContainer>
        ))}
      </TaskPhotosContainer>
      <GalleryContainer
        initialAsset={initialGalleryAsset}
        assets={taskImages}
        onRequestClose={() => setIsGalleryOpen(false)}
        isOpen={isGalleryOpen}
      />
    </>
  );
};

TaskAssets.propTypes = {
  task: PropTypes.object,
  toggleTaskStatus: PropTypes.func,
};

export default TaskAssets;
